/* Compact option values resolve normal CBB selections; full matrices are exceptional. */
(() => {
  'use strict';
  const number = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    const result = Number(value);
    return Number.isFinite(result) ? result : null;
  };
  const positive = (value) => { const n = number(value); return n !== null && n > 0 ? n : null; };
  class SourceError extends Error {
    constructor(message, options = {}) { super(message); Object.assign(this, options); }
  }
  function resolve(products, width, height) {
    if (!positive(width) || !positive(height) || !products.length) throw new SourceError('Medidas inválidas.');
    let winner = null;
    let distance = Infinity;
    for (const product of products) {
      if (!product.complete || !product.variants?.length) throw new SourceError('Dados incompletos.');
      for (const variant of product.variants) {
        if (!positive(variant.width) || !positive(variant.height)) throw new SourceError('Variante sem medidas válidas.');
        const dw = variant.width - width;
        const dh = variant.height - height;
        if (dw >= 0 && dh >= 0 && dw + dh < distance) {
          winner = { product, variant }; distance = dw + dh;
        }
      }
    }
    return winner;
  }
  // Preview quote only; the Shopify Function independently validates the product grids.
  const gridBudget = () => ({ work: 100000, cells: 1000, bytes: 24000, cache: new Set() });
  function pricegrid(record, width, height, budget = gridBudget()) {
    budget.work -= 2048;
    if (budget.work < 0) return null;
    if (record.pricegridEnabled !== true || record.currency !== 'BRL' ||
      ![width, height].every(value => Number.isSafeInteger(value) && value > 0) ||
      width > record.width || height > record.height) return null;
    const price = record.pricegrid; const cost = record.costgrid;
    const object = value => value && typeof value === 'object' && !Array.isArray(value);
    const dimension = key => /^[1-9]\d*$/.test(key) && Number.isSafeInteger(Number(key));
    const money = value => typeof value === 'number' && value > 0 && Number.isSafeInteger(Math.round(value * 100)) && Math.round(value * 100) / 100 === value;
    const gid = value => typeof value === 'string' && /^gid:\/\/shopify\/Product\/[1-9]\d*$/.test(value);
    const header = (grid, isPrice) => object(grid)
      && (grid.version === 2
        ? Object.keys(grid).sort().join(',') === (isPrice ? 'discountPercent,prices,shopifyProductId,uploadedAt,version' : 'prices,shopifyProductId,uploadedAt,version')
          && gid(grid.shopifyProductId)
          && (!isPrice || typeof grid.discountPercent === 'number' && Number.isFinite(grid.discountPercent) && grid.discountPercent >= 0 && grid.discountPercent < 100)
        : Object.keys(grid).sort().join(',') === 'prices,sku,uploadedAt' && typeof grid.sku === 'string' && grid.sku.length > 0 && grid.sku.trim() === grid.sku)
      && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(grid.uploadedAt)
      && Number.isFinite(Date.parse(grid.uploadedAt)) && new Date(grid.uploadedAt).toISOString() === grid.uploadedAt
      && object(grid.prices) && Object.keys(grid.prices).length > 0
      && new TextEncoder().encode(JSON.stringify(grid)).length <= 10000;
    if (!header(price, true) || !header(cost, false) || price.version !== cost.version ||
      price.sku !== cost.sku || price.shopifyProductId !== cost.shopifyProductId || price.uploadedAt !== cost.uploadedAt ||
      Object.keys(price.prices).sort().join(',') !== Object.keys(cost.prices).sort().join(',')) return null;
    const sku = /^(.*)-(\d{3}|\d{4})-(\d{3}|\d{4})$/.exec(record.sku || '');
    if (!sku || !sku[1] || width > Number(sku[2]) * (sku[2].length === 3 ? 10 : 1) ||
      height > Number(sku[3]) * (sku[3].length === 3 ? 10 : 1)) return null;
    if (price.version === 2) {
      const productId = String(record.productId);
      if (price.shopifyProductId !== (gid(productId) ? productId : `gid://shopify/Product/${productId}`) ||
        ![record.width, record.height].every(value => Number.isSafeInteger(value) && value > 0)) return null;
    } else {
      if (!sku || !sku[1] || price.sku !== sku[1] ||
        Number(sku[2]) * (sku[2].length === 3 ? 10 : 1) !== record.width ||
        Number(sku[3]) * (sku[3].length === 3 ? 10 : 1) !== record.height) return null;
    }
    for (const [w, row] of Object.entries(price.prices)) {
      const costs = cost.prices[w];
      if (!dimension(w) || !object(row) || !object(costs) || !Object.keys(row).length ||
        Object.keys(row).sort().join(',') !== Object.keys(costs).sort().join(',')) return null;
      for (const [h, value] of Object.entries(row)) {
        if (!dimension(h) || !money(value) || !money(costs[h]) || value < costs[h] ||
          price.version === 2 && !Number.isSafeInteger(Math.round(value * 100 / (1 - price.discountPercent / 100)))) return null;
      }
    }
    const w = Object.keys(price.prices).map(Number).filter(value => value >= width).sort((a, b) => a - b)[0];
    if (w === undefined) return null;
    // ponytail: an incomplete nearest width row falls back to native; no synthetic cells or row skipping.
    const h = Object.keys(price.prices[w]).map(Number).filter(value => value >= height).sort((a, b) => a - b)[0];
    if (h === undefined) return null;
    const key = JSON.stringify([price, cost]);
    const bytes = new TextEncoder().encode(JSON.stringify(price)).length + new TextEncoder().encode(JSON.stringify(cost)).length;
    budget.work -= bytes;
    if (!budget.cache.has(key)) {
      const cells = Object.values(price.prices).reduce((sum, row) => sum + Object.keys(row).length, 0);
      budget.bytes -= bytes;
      budget.cells -= cells * 2;
      budget.work -= (Object.keys(price.prices).length + cells) * 128;
      budget.cache.add(key);
    }
    budget.work -= (Object.keys(price.prices).length + Object.keys(price.prices[w]).length) * 64;
    if (budget.work < 0 || budget.bytes < 0 || budget.cells < 0) return null;
    const cents = Math.round(price.prices[w][h] * 100);
    return Object.freeze({ width: w, height: h, price: cents, cost: cost.prices[w][h].toFixed(2),
      ...(price.version === 2 ? { compareAtPrice: Math.round(cents / (1 - price.discountPercent / 100)), discountPercent: price.discountPercent } : {}) });
  }
  function compact(product) {
    if (product.optionCount !== 1 || product.optionName?.toLowerCase() !== 'cod:' ||
      !Number.isInteger(product.count) || product.count < 1 || !Array.isArray(product.codes) ||
      product.codes.length !== product.count) return null;
    const ids = new Set(); const names = new Set(); const variants = [];
    for (const code of product.codes) {
      const match = /^(\d{3,4})-(\d{3,4})$/.exec(code.name || '');
      if (!/^\d+$/.test(code.id || '') || !match || ids.has(code.id) || names.has(code.name)) return null;
      ids.add(code.id); names.add(code.name);
      variants.push({ optionValueId: code.id, width: Number(match[1]) * 10, height: Number(match[2]) * 10 });
    }
    return { ...product, variants, complete: true, compact: true };
  }
  function technical(winner, rows, field) {
    const valid = (value) => { const n = number(value); return n !== null && n >= 0 ? n : null; };
    const direct = valid(winner[field]);
    if (direct !== null) return direct;
    const same = rows.find(row => row.width === winner.width && valid(row[field]) !== null);
    if (same) return valid(same[field]);
    const values = new Set(rows.map(row => valid(row[field])).filter(value => value !== null));
    return values.size === 1 ? [...values][0] : null;
  }
  class Source {
    constructor(config, transport = (...args) => fetch(...args), { manifestSeed = null } = {}) {
      this.config = config; this.transport = transport; this.cache = new Map();
      this.manifestSeed = manifestSeed;
      this.queue = []; this.active = 0; this.suspendedUntil = 0; this.disposed = false;
      this.storefrontUnavailable = false;
      this.lifecycle = new AbortController();
    }
    dispose() { this.disposed = true; this.lifecycle.abort(); this.cache.clear(); }
    async read(url, options = {}, signal) {
      return this.slot(() => this.readDirect(url, options, signal), signal);
    }
    async readDirect(url, options = {}, signal) {
      if (this.disposed || signal?.aborted) throw new DOMException('Cancelado', 'AbortError');
      if (Date.now() < this.suspendedUntil) throw new SourceError('Limite temporário de consultas. Aguarde antes de tentar novamente.', { status: 429 });
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal?.addEventListener('abort', abort, { once: true });
      this.lifecycle.signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 10000);
      try {
        const response = await this.transport(url, { ...options, signal: controller.signal });
        if (response.status === 429) {
          const retry = response.headers.get('Retry-After')?.trim();
          const delay = retry && /^\d+$/.test(retry) ? Number(retry) * 1000 : Date.parse(retry) - Date.now();
          this.suspendedUntil = Date.now() + (Number.isFinite(delay) && delay >= 0 ? delay : 60000);
          throw new SourceError('Limite temporário de consultas. Aguarde antes de tentar novamente.', { status: 429 });
        }
        if (!response.ok) throw new SourceError('Não foi possível consultar o produto.', { status: response.status });
        // Include the complete response body in the read timeout.
        return { text: await response.text(), url: response.url };
      } finally {
        clearTimeout(timeout); signal?.removeEventListener('abort', abort);
        this.lifecycle.signal.removeEventListener('abort', abort);
      }
    }
    async slot(operation, signal) {
      if (this.active >= 2) await new Promise(resolve => this.queue.push(resolve));
      this.active++;
      try {
        if (this.disposed || signal?.aborted) throw new DOMException('Cancelado', 'AbortError');
        return await operation();
      } finally { this.active--; this.queue.shift()?.(); }
    }
    matrix(product, technicalOnly = false, signal) {
      const key = `${product.id}:${this.config.token ? (technicalOnly ? 'technical' : 'dimensions') : 'native'}`;
      if (!this.cache.has(key)) {
        const pending = this.pages(product, technicalOnly, signal).catch(error => {
          if (this.cache.get(key) === pending) this.cache.delete(key);
          throw error;
        });
        this.cache.set(key, pending);
      }
      return this.cache.get(key);
    }
    async candidates(products) {
      const indexed = products.map(compact);
      if (indexed.every(Boolean)) return indexed;
      // A malformed code in any related product can change the commercial
      // winner. Fall back for the whole color group, with the old ordering.
      const batch = new AbortController();
      const pending = products.map(product => this.matrix(product, false, batch.signal));
      try {
        return await Promise.all(pending);
      } catch (error) {
        batch.abort();
        // Drain cancelled reads before making the failed group retryable.
        await Promise.allSettled(pending);
        throw error;
      }
    }
    async pages(product, technicalOnly, signal) {
      if (!this.config.token || this.storefrontUnavailable) return this.nativePages(product, signal);
      const fields = technicalOnly
        ? 'deduction:metafield(namespace:"custom",key:"erp-cbb_diference-bracket-fabric-lenght"){value} depth:metafield(namespace:"custom",key:"erp-cbb_blind-dept-in-milimeters"){value}'
        : 'height:metafield(namespace:"custom",key:"_erp_cbb_altura"){value}';
      const query = `query CbbMatrix($id:ID!,$after:String,$country:CountryCode!,$language:LanguageCode!){product(id:$id){id variantsCount{count} variants(first:250,after:$after,sortKey:POSITION){nodes{id width:metafield(namespace:"custom",key:"_erp_cbb_largura"){value} ${fields}}pageInfo{hasNextPage endCursor}}}}`;
      // Market/language context also applies to visibility, not only price.
      const contextualQuery = query.replace('){product', ') @inContext(country:$country,language:$language){product');
      const rows = []; const ids = new Set(); const cursors = new Set(); let after = null; let expected = null;
      for (;;) {
        const result = await this.read(`https://${this.config.shop}/api/2026-07/graphql.json`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': this.config.token },
          body: JSON.stringify({ query: contextualQuery, variables: { id: `gid://shopify/Product/${product.id}`, after, country: this.config.country, language: this.config.language } }),
        }, signal);
        const data = JSON.parse(result.text);
        const p = data.data?.product;
        if (data.errors?.length) throw new SourceError('Produto não acessível ou resposta incompleta.');
        // Products can be available on Online Store while this public token
        // cannot see them. The theme section still provides an authoritative
        // matrix; keep the same confirmation step after selecting a variant.
        if (p === null) {
          this.storefrontUnavailable = true;
          return this.nativePages(product, signal);
        }
        if (!p || p.id !== `gid://shopify/Product/${product.id}`) throw new SourceError('Produto não acessível ou resposta incompleta.');
        const count = p.variantsCount?.count;
        if (!Number.isInteger(count) || count < 1 || (expected !== null && expected !== count)) throw new SourceError('A matriz mudou durante a consulta.');
        expected = count;
        const page = p.variants;
        if (!Array.isArray(page?.nodes) || !page.nodes.length || page.nodes.length > 250 || typeof page.pageInfo?.hasNextPage !== 'boolean') throw new SourceError('Página de variantes inválida.');
        for (const row of page.nodes) {
          if (!/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(row.id) || ids.has(row.id)) throw new SourceError('Variante duplicada ou inválida.');
          ids.add(row.id);
          const variant = { id: row.id.split('/').pop(), width: positive(row.width?.value) };
          if (!variant.width) throw new SourceError('Variante sem largura válida.');
          if (technicalOnly) {
            variant.depth = number(row.depth?.value); variant.deduction = number(row.deduction?.value);
          } else {
            variant.height = positive(row.height?.value);
            if (!variant.height) throw new SourceError('Variante sem altura válida.');
          }
          rows.push(variant);
        }
        if (rows.length > expected) throw new SourceError('Contagem de variantes inconsistente.');
        if (!page.pageInfo.hasNextPage) break;
        after = page.pageInfo.endCursor;
        if (!after || cursors.has(after)) throw new SourceError('Paginação de variantes inconsistente.');
        cursors.add(after);
      }
      if (rows.length !== expected || (product.count && product.count !== expected)) throw new SourceError('Nem todas as variantes foram recebidas.');
      return { ...product, variants: rows, complete: true };
    }
    async nativePages(product, signal) {
      const firstURL = this.sectionURL(product.url, 'fp-cbb-matrix');
      firstURL.searchParams.set('page', '1');
      const controller = new AbortController();
      const abort = () => controller.abort();
      if (signal?.aborted) abort();
      signal?.addEventListener('abort', abort, { once: true });
      let validatePage;
      const fetchPage = async page => {
        const url = new URL(firstURL);
        url.searchParams.set('page', String(page));
        const result = await this.read(url.toString(), {}, controller.signal);
        const data = this.parseSection(result.text, '[data-fp-cbb-matrix]');
        validatePage?.(data, page);
        return data;
      };
      try {
        const first = await fetchPage(1);
        const count = first.count;
        if (!Number.isInteger(count) || count < 1 || (product.count && product.count !== count)) throw new SourceError('A matriz mudou durante a consulta.');
        const pages = Math.ceil(count / 50);
        const validate = (data, page) => {
          if (String(data.productId) !== String(product.id) || data.count !== count || data.page !== page || data.pages !== pages)
            throw new SourceError('A matriz mudou durante a consulta.');
          if (!Array.isArray(data.variants) || data.variants.length !== Math.min(50, count - (page - 1) * 50))
            throw new SourceError('Página de variantes inválida.');
          if (data.variants.some(row => !positive(row.width) || !positive(row.height)))
            throw new SourceError('Variante sem medidas válidas.');
          if (page < pages) {
            if (!data.next || Number(this.sectionURL(data.next, 'fp-cbb-matrix').searchParams.get('page')) !== page + 1)
              throw new SourceError('Paginação de variantes inconsistente.');
          } else if (data.next) throw new SourceError('Paginação de variantes inconsistente.');
          return data.variants;
        };
        validate(first, 1);
        validatePage = validate;
        const pending = Array.from({ length: pages - 1 }, (_, index) => fetchPage(index + 2));
        const results = await Promise.all(pending);
        const rows = []; const ids = new Set();
        [first, ...results].forEach((data, index) => {
          for (const row of validate(data, index + 1)) {
            if (!/^\d+$/.test(row.id) || ids.has(row.id)) throw new SourceError('Variante duplicada ou inválida.');
            const width = positive(row.width); const height = positive(row.height);
            if (!width || !height) throw new SourceError('Variante sem medidas válidas.');
            ids.add(row.id);
            rows.push({ id: row.id, width, height, depth: number(row.depth), deduction: number(row.deduction) });
          }
        });
        if (rows.length !== count) throw new SourceError('Nem todas as variantes foram recebidas.');
        return { ...product, variants: rows, complete: true };
      } catch (error) { controller.abort(); throw error; }
      finally { signal?.removeEventListener('abort', abort); }
    }
    sectionURL(path, section, variant) {
      const url = new URL(path, location.href);
      if (url.origin !== location.origin) throw new SourceError('Endereço de produto inválido.');
      const current = new URL(location.href);
      for (const name of ['preview_theme_id', '_fd', 'pb', 'locale', 'country', 'currency']) {
        if (current.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name));
      }
      // Shopify may strip preview_theme_id from the address bar after opening
      // a draft. Section requests still need that ID or they hit the live theme.
      const theme = typeof window === 'object' ? window.Shopify?.theme : null;
      if (theme?.id && theme.role === 'unpublished') url.searchParams.set('preview_theme_id', String(theme.id));
      url.searchParams.set('section_id', section);
      if (variant?.optionValueId) url.searchParams.set('option_values', variant.optionValueId);
      else if (variant?.id) url.searchParams.set('variant', variant.id);
      return url;
    }
    parseSection(text, selector) {
      const document = new DOMParser().parseFromString(text, 'text/html');
      const nodes = document.querySelectorAll(selector);
      if (nodes.length !== 1 || text.includes('Liquid error')) throw new SourceError('Resposta do produto inválida.');
      return JSON.parse(nodes[0].textContent);
    }
    async manifest() {
      const products = []; const ids = new Set(); const seen = new Set();
      let url = this.sectionURL(this.config.productURL, 'fp-cbb-manifest'); let page = 1; let total = null; let items = null;
      const seed = this.manifestSeed;
      this.manifestSeed = null;
      for (;;) {
        const age = seed ? Date.now() - seed.at : -1;
        const result = page === 1 && seed?.url === url.toString() && age >= 0 && age < 20000
          ? { text: seed.text, url: seed.url } : await this.read(url.toString());
        const data = this.parseSection(result.text, '[data-fp-cbb-manifest]');
        if (String(data.product) !== String(this.config.productId) || String(data.collection) !== String(this.config.collectionId) || data.page !== page || !Number.isInteger(data.pages) || data.pages < page || (total !== null && data.pages !== total) || !Array.isArray(data.products)) throw new SourceError('Coleção de medidas incompleta.');
        total = data.pages;
        if (!Number.isInteger(data.items) || data.items < 1 || (items !== null && items !== data.items) || Math.ceil(data.items / 250) !== data.pages || data.products.length !== Math.min(250, data.items - (page - 1) * 250)) throw new SourceError('Contagem da coleção inconsistente.');
        items = data.items;
        for (const product of data.products) {
          if (!/^\d+$/.test(String(product.id)) || ids.has(product.id)) throw new SourceError('Produto duplicado no manifesto.');
          ids.add(product.id);
          if (['persiana_principal', 'persiana_secundaria'].includes(product.type) && product.color === this.config.color) products.push(product);
        }
        if (page === total) break;
        if (!data.next || seen.has(data.next)) throw new SourceError('Paginação da coleção incompleta.');
        seen.add(data.next); url = this.sectionURL(data.next, 'fp-cbb-manifest'); page++;
      }
      if (ids.size !== items || !products.length) throw new SourceError('Nenhum conjunto completo para essas medidas.');
      return products;
    }
    async confirm(product, variant, signal, fresh = false) {
      const url = this.sectionURL(product.url, 'fp-cbb-confirmation', variant);
      if (fresh) url.searchParams.set('_fp_grid_check', String(Date.now()));
      const result = await this.read(url.toString(), fresh ? { cache: 'no-store' } : {}, signal);
      const data = this.parseSection(result.text, '[data-fp-cbb-confirmation]');
      if (!Object.hasOwn(data, 'compare_at_price') || (data.compare_at_price !== null && (!Number.isFinite(data.compare_at_price) || data.compare_at_price < 0))) throw new SourceError('Preço de comparação inválido.');
      if (String(data.productId) !== String(product.id) || !/^\d+$/.test(String(data.id)) || (variant.id && String(data.id) !== String(variant.id)) || data.width !== variant.width || data.height !== variant.height || !Number.isFinite(data.price) || data.price < 0 || !/^[A-Z]{3}$/.test(data.currency) || data.currency !== this.config.currency || typeof data.available !== 'boolean' || !positive(data.quantityRule?.min) || !positive(data.quantityRule?.increment) || (data.quantityRule.max !== null && !positive(data.quantityRule.max))) throw new SourceError('Não foi possível confirmar a variante selecionada.');
      if (data.pricegridEnabled === true && !fresh) return this.confirm(product, variant, signal, true);
      return data;
    }
    async cartPricegrid(record, width, height, signal, fresh = false) {
      if (fresh) record = await this.confirm({ id: record.productId, url: record.productURL }, { id: record.id, width: record.width, height: record.height }, signal, true);
      if (!pricegrid(record, width, height)) return null;
      const result = await this.read(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' }, signal);
      const cart = JSON.parse(result.text);
      if (!Array.isArray(cart.items) || cart.currency !== record.currency) throw new SourceError('Não foi possível confirmar o preço no carrinho.');
      const budget = gridBudget();
      for (const item of cart.items) {
        const props = item.properties || {};
        if (props._usePricegrid !== 'true') continue;
        if (budget.work < 2048) return null;
        if (item.selling_plan_allocation || item.parent_relationship ||
          ['_cart_transform', '_motor_id', '_motor_price', '_cassete_id', '_cassete_price', '_nodrills_id', '_nodrills_price', '_cardboard_box_id'].some(key => props[key])) {
          budget.work -= 2048; continue;
        }
        const valid = value => typeof value === 'string' && /^[1-9]\d*$/.test(value) && Number.isSafeInteger(Number(value));
        if (![props._width, props._drop].every(valid) || props._width !== props.Largura || props._drop !== props.Altura) {
          budget.work -= 2048; continue;
        }
        const url = this.sectionURL(item.url, 'fp-cbb-confirmation', { id: String(item.variant_id) });
        url.searchParams.set('_fp_grid_check', String(Date.now()));
        const response = await this.read(url.toString(), { cache: 'no-store' }, signal);
        const confirmed = this.parseSection(response.text, '[data-fp-cbb-confirmation]');
        if (String(confirmed.id) !== String(item.variant_id) || String(confirmed.productId) !== String(item.product_id) || confirmed.currency !== record.currency) throw new SourceError('O carrinho retornou um produto diferente.');
        const existing = pricegrid(confirmed, Number(props._width), Number(props._drop), budget);
        if (confirmed.pricegridEnabled === true && !existing) return null;
      }
      // ponytail: reserve a separate line even if Shopify merges the add; avoids underestimating work.
      return pricegrid(record, width, height, budget);
    }
  }
  const api = { Source, SourceError, resolve, compact, technical, number, positive, pricegrid };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else window.FPCbb = api;
})();
